import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

/**
 * Centralized service for checking block status between users.
 * Used by Business Rules Engine and other services to enforce blocking.
 */
class BlockService {
  private static instance: BlockService;
  
  // Cache for block checks to avoid repeated DB calls
  private blockCache: Map<string, { isBlocked: boolean; isBlockedBy: boolean; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 30000; // 30 seconds

  static getInstance(): BlockService {
    if (!BlockService.instance) {
      BlockService.instance = new BlockService();
    }
    return BlockService.instance;
  }

  private getCacheKey(userId: string, targetId: string): string {
    return `${userId}:${targetId}`;
  }

  /**
   * Check if there's any block between two users (either direction)
   */
  async checkBlockStatus(userId: string, targetId: string): Promise<{ isBlocked: boolean; isBlockedBy: boolean }> {
    if (!userId || !targetId || userId === targetId) {
      return { isBlocked: false, isBlockedBy: false };
    }

    const cacheKey = this.getCacheKey(userId, targetId);
    const cached = this.blockCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return { isBlocked: cached.isBlocked, isBlockedBy: cached.isBlockedBy };
    }

    try {
      // Check if userId blocked targetId
      const { data: isBlockedData } = await supabase.rpc('check_is_blocked', {
        p_user_id: userId,
        p_target_id: targetId
      });

      // Check if targetId blocked userId
      const { data: isBlockedByData } = await supabase.rpc('check_is_blocked_by', {
        p_user_id: userId,
        p_target_id: targetId
      });

      const result = {
        isBlocked: !!isBlockedData,
        isBlockedBy: !!isBlockedByData
      };

      // Cache the result
      this.blockCache.set(cacheKey, {
        ...result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      debugLogger.error('[BlockService] Error checking block status:', error);
      return { isBlocked: false, isBlockedBy: false };
    }
  }

  /**
   * Check if any block exists between two users (either direction)
   */
  async isAnyBlockBetween(userId: string, targetId: string): Promise<boolean> {
    const { isBlocked, isBlockedBy } = await this.checkBlockStatus(userId, targetId);
    return isBlocked || isBlockedBy;
  }

  /**
   * Get all user IDs that the given user has blocked
   */
  async getBlockedUserIds(userId: string): Promise<string[]> {
    if (!userId) return [];

    try {
      const { data, error } = await supabase
        .from('user_blocks')
        .select('blocked_id')
        .eq('blocker_id', userId);

      if (error) {
        debugLogger.error('[BlockService] Error getting blocked users:', error);
        return [];
      }

      return data?.map(row => row.blocked_id) || [];
    } catch (error) {
      debugLogger.error('[BlockService] Error getting blocked users:', error);
      return [];
    }
  }

  /**
   * Get all user IDs that have blocked the given user
   */
  async getBlockedByUserIds(userId: string): Promise<string[]> {
    if (!userId) return [];

    try {
      // Using RPC since RLS prevents direct SELECT on blocked_id
      const { data, error } = await supabase.rpc('get_users_who_blocked_me');

      if (error) {
        debugLogger.error('[BlockService] Error getting blocked by users:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      debugLogger.error('[BlockService] Error getting blocked by users:', error);
      return [];
    }
  }

  /**
   * Get all blocked user IDs (both directions combined)
   */
  async getAllBlockedUserIds(userId: string): Promise<string[]> {
    const [blocked, blockedBy] = await Promise.all([
      this.getBlockedUserIds(userId),
      this.getBlockedByUserIds(userId)
    ]);
    
    return [...new Set([...blocked, ...blockedBy])];
  }

  /**
   * Invalidate cache for a specific user pair
   */
  invalidateCache(userId: string, targetId?: string): void {
    if (targetId) {
      this.blockCache.delete(this.getCacheKey(userId, targetId));
      this.blockCache.delete(this.getCacheKey(targetId, userId));
    } else {
      // Clear all cache entries involving this user
      for (const key of this.blockCache.keys()) {
        if (key.includes(userId)) {
          this.blockCache.delete(key);
        }
      }
    }
  }
}

export const blockService = BlockService.getInstance();
